import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronsDown, ChevronsUp } from 'lucide-react'
import React from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { SelectFormField } from 'src/shared/components/ui/forms/form-fields/select'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import {
  propertyTypeSchema,
  propertyTypeMap,
  titleTransactionTypeSchema,
  titleTransactionTypeMap,
  dateFlexibilitySchema,
  dateFlexibilityMap,
} from 'src/shared/schemas/referrals'
import { useEventStore } from 'src/shared/store/event-store'

const titleRepSchema = z.object({
  titleTransactionType: titleTransactionTypeSchema,
  propertyType: propertyTypeSchema,
  desiredClosingDate: z
    .date()
    .refine((date) => date > new Date(), {
      message: 'Closing date must be in the future',
    })
    .optional(),
  dateFlexibility: dateFlexibilitySchema.optional(),
  additionalServices: z.array(z.string()).optional(),
  approvedLoanAmount: z.number().optional(),
  downPaymentEstimate: z.number().optional(),
})

type TitleRepFormData = z.infer<typeof titleRepSchema>

export const TitleRepInfo: React.FC = () => {
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId
  const selectedReferral = useSendReferralStore((state) => state.selectedReferral)
  const [showOptional, setShowOptional] = React.useState(false)

  const form = useForm<TitleRepFormData>({
    resolver: zodResolver(titleRepSchema),
    mode: 'onChange',
    defaultValues: {
      titleTransactionType:
        referralFormData.titleTransactionType || selectedReferral?.titleTransactionType || undefined,
      propertyType: referralFormData.propertyType || selectedReferral?.propertyType || undefined,
      desiredClosingDate: referralFormData.desiredClosingDate || selectedReferral?.desiredClosingDate || undefined,
      dateFlexibility: referralFormData.dateFlexibility || selectedReferral?.dateFlexibility || undefined,
      additionalServices: referralFormData.additionalServices || selectedReferral?.additionalServices || [],
      approvedLoanAmount: referralFormData.approvedLoanAmount || selectedReferral?.approvedLoanAmount || undefined,
      downPaymentEstimate: referralFormData.downPaymentEstimate || selectedReferral?.downPaymentEstimate || undefined,
    },
  })

  const onSubmit = async (data: z.infer<typeof titleRepSchema>) => {
    if (currentUser) {
      sendEvent({
        userId: currentUser.id,
        eventType: 'flow_step',
        info: {
          sessionId,
          flowType: 'send_referral',
          flowStep: 'submit_title_rep_info',
          flowId,
          ...data,
        },
      })
    }

    handleNext(data)
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 flex flex-col gap-1">
        <SelectFormField
          control={form.control}
          name="titleTransactionType"
          label="Transaction Type"
          options={titleTransactionTypeMap}
          required
        />
        <SelectFormField
          control={form.control}
          name="propertyType"
          label="Property Type"
          options={propertyTypeMap}
          required
        />

        <Button
          variant="link"
          type="button"
          onClick={() => setShowOptional(!showOptional)}
          className="flex w-full items-center justify-center gap-2"
        >
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />} Optional Info{' '}
          {showOptional ? <ChevronsUp size={20} /> : <ChevronsDown size={20} />}
        </Button>

        {showOptional && (
          <>
            <InputFormField
              control={form.control}
              name="desiredClosingDate"
              label="Desired Closing Date"
              type="date"
              floatingLabel={false}
              fieldClassName="bg-accent"
            />

            <SelectFormField
              control={form.control}
              name="dateFlexibility"
              label="Date Flexibility"
              options={dateFlexibilityMap}
            />

            {/* TODO: <ListFormField
              control={form.control}
              name="additionalServices"
              label="Additional Services"
              placeholder="Type and press enter or click plus..."
              maxLength={100}
            /> */}

            <InputFormField
              control={form.control}
              name="approvedLoanAmount"
              label="Loan Amount"
              type="currency"
              floatingLabel={false}
            />
            <InputFormField
              control={form.control}
              name="downPaymentEstimate"
              label="Down Payment"
              type="currency"
              floatingLabel={false}
              checkbox={{ label: 'Not sure', defaultChecked: false }}
            />
          </>
        )}

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button type="submit" className="w-full">
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
